/* eslint-disable camelcase */
/* eslint-disable no-restricted-syntax */


const getBaseVideoItem = (workspaceItems) => {
  for (const item of Object.values(workspaceItems)) {
    if (item.type === "UPVIDEO" && !item.sceneId) {
      return item;
    }
  }
  return null;
}

/**
 * Computes workspace x/y/width/height for a short clip video item.
 * Uses object-fit: cover scaling (same as buildVideoBlobMeta) and centres
 * on the reframe focus point when reframe data is present.
 *
 * @param {object} baseVideoItem - Source UPVIDEO workspace item (for pwidth/pheight)
 * @param {object|null} reframe - { x, y } normalised focus centre (0–1), or null
 * @param {number} projectWidth
 * @param {number} projectHeight
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const getReframeWorkspacePosition = (
  baseVideoItem,
  reframe,
  projectWidth,
  projectHeight
) => {
  const sourceWidth = baseVideoItem.pwidth || baseVideoItem.width;
  const sourceHeight = baseVideoItem.pheight || baseVideoItem.height;

  let scaledWidth = projectWidth;
  let scaledHeight = (sourceHeight / sourceWidth) * scaledWidth;

  if (scaledHeight < projectHeight) {
    scaledHeight = projectHeight;
    scaledWidth = (sourceWidth / sourceHeight) * scaledHeight;
  }

  let x = (projectWidth - scaledWidth) / 2;
  let y = (projectHeight - scaledHeight) / 2;

  if (reframe && Number.isFinite(reframe.x) && Number.isFinite(reframe.y)) {
    const focusX = reframe.x * scaledWidth;
    const focusY = reframe.y * scaledHeight;

    x = projectWidth / 2 - focusX;
    y = projectHeight / 2 - focusY;

    const minX = projectWidth - scaledWidth;
    const maxX = 0;
    const minY = projectHeight - scaledHeight;
    const maxY = 0;

    x = Math.min(maxX, Math.max(minX, x));
    y = Math.min(maxY, Math.max(minY, y));
  }

  return { x, y, width: scaledWidth, height: scaledHeight };
};

/**
 * Source-video crop region for thumbnail extraction (mirrors getReframeWorkspacePosition).
 *
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @param {number} targetRatio - Output width / height (e.g. 9/16 for vertical)
 * @param {object|null} reframe - { x, y } normalised focus centre (0–1), or null
 * @returns {{ sx: number, sy: number, sw: number, sh: number }}
 */
export const getReframeCropRegion = (
  sourceWidth,
  sourceHeight,
  targetRatio,
  reframe
) => {
  let cropWidth;
  let cropHeight;
  const sourceRatio = sourceWidth / sourceHeight;

  if (sourceRatio > targetRatio) {
    cropHeight = sourceHeight;
    cropWidth = sourceHeight * targetRatio;
  } else {
    cropWidth = sourceWidth;
    cropHeight = sourceWidth / targetRatio;
  }

  let sx = (sourceWidth - cropWidth) / 2;
  let sy = (sourceHeight - cropHeight) / 2;

  if (reframe && Number.isFinite(reframe.x) && Number.isFinite(reframe.y)) {
    sx = reframe.x * sourceWidth - cropWidth / 2;
    sy = reframe.y * sourceHeight - cropHeight / 2;
    sx = Math.min(sourceWidth - cropWidth, Math.max(0, sx));
    sy = Math.min(sourceHeight - cropHeight, Math.max(0, sy));
  }

  return { sx, sy, sw: cropWidth, sh: cropHeight };
};

/**
 * Resolves reframe data for a short at a given time.
 * Per-clip reframe takes priority; falls back to short-level reframe.
 *
 * @param {object} shortData
 * @param {number} [timeAt]
 * @returns {object|null}
 */
export const getShortReframe = (shortData, timeAt) => {
  const clips = shortData?.clips;
  const shortReframe = shortData?.reframe;

  if (clips?.length > 0) {
    const time = Number.isFinite(timeAt) ? timeAt : shortData.start;
    const matchingClip = clips.find(
      (clip) => time >= clip.startTime && time < clip.endTime
    );
    const clip = matchingClip || clips[0];
    return clip?.reframe || shortReframe || null;
  }

  return shortReframe || null;
};

const getWithinRangeWorkspaceItems = (workspaceItems, start, end) => {
  const items = [];
  for (const item of Object.values(workspaceItems)) {
    const isBaseVideo = item.type === "UPVIDEO" && !item.sceneId;
    const isReframeData = item.type === "UPVIDEO" && item.sceneId;
    if (!isBaseVideo && !isReframeData && ((item.videoStart >= start && item.videoEnd <= end) ||
      (item.videoStart <= start && item.videoEnd > start) ||
      (item.videoStart < end && item.videoStart > start))) {
      items.push(item);
    }
  }
  items.sort((a, b) => a.videoStart - b.videoStart);
  let timelineStart = 0;
  items.forEach((item) => {
    const videoStart = Math.max(item.videoStart, start);
    const videoEnd = Math.min(item.videoEnd, end);
    const duration = videoEnd - videoStart;
    item.videoStart = videoStart;
    item.videoEnd = videoEnd;
    item.enterStart = timelineStart;
    item.enterEnd = timelineStart;
    item.exitStart = timelineStart + duration;
    item.exitEnd = timelineStart + duration;
    timelineStart += duration;
  });
  return items;
}

/**
 * Function to return the project data by modefying the id of a workspace item.
 * @param {object} projectData
 * @param {object} shortData
 * @returns An object of a new project.
 */
export const getNewProject = (projectData, shortData) => {

  const baseVideoItem = getBaseVideoItem(projectData.workspaceItems);

  const updatedData = structuredClone(projectData);
  const updatedWorkspaceItems = {};

  const clips =
    shortData?.clips?.length > 0
      ? shortData.clips
      : [
        {
          startTime: shortData.start,
          endTime: shortData.end,
        },
      ];

  clips.forEach((element, index) => {
    element.id = `${shortData.id}_clip${index}`;
  });



  let timelineStart = 0;
  let prodDuration = 0;

  const { width: projectWidth, height: projectHeight } = projectData;

  clips.forEach((clip) => {
    const { startTime, endTime, id } = clip;
    const reframe = clip.reframe || shortData.reframe;
    const duration = endTime - startTime;
    prodDuration += duration;

    const { x, y, width, height } = getReframeWorkspacePosition(
      baseVideoItem,
      reframe,
      projectWidth,
      projectHeight
    );

    updatedWorkspaceItems[id] = {
      ...structuredClone(baseVideoItem),
      id,
      x,
      y,
      width,
      height,
      videoStart: startTime,
      videoEnd: endTime,
      enterStart: timelineStart,
      enterEnd: timelineStart,
      exitStart: timelineStart + duration,
      exitEnd: timelineStart + duration
    };
    const clipBasedItems = getWithinRangeWorkspaceItems(projectData.workspaceItems, startTime, endTime);
    clipBasedItems.forEach((item) => {
      updatedWorkspaceItems[`${item.id}_${id}`] = {
        ...structuredClone(item),
        id: `${item.id}_${id}`,
        drop: `${item.id}_${id}`,
        dropId: id,
        originalId: id,
      };
    });

    timelineStart += duration;
  });

  // delete updatedWorkspaceItems[videoKey];

  updatedData.workspaceItems = updatedWorkspaceItems;
  updatedData.duration = prodDuration;
  return updatedData;
};

export const getShortsPlayerDimensions = (
  dimensionName,
  projectWidth,
  projectHeight
) => {
  let effectiveZoom;

  if (dimensionName === "vertical") {
    const maxVerticalHeight = 500;
    const scaleToFit = maxVerticalHeight / projectHeight;
    effectiveZoom = Math.min(scaleToFit, 1);
  } else if (dimensionName === "square") {
    const maxSize = 534;
    const scaleToFit = maxSize / Math.max(projectWidth, projectHeight);
    effectiveZoom = Math.min(scaleToFit, 1);
  } else {
    const maxWidth = 540;
    const maxHeight = 534;
    const scaleX = maxWidth / projectWidth;
    const scaleY = maxHeight / projectHeight;
    effectiveZoom = Math.min(scaleX, scaleY);
  }

  return {
    effectiveZoom,
    containerWidth: Math.round(projectWidth * effectiveZoom),
    containerHeight: Math.round(projectHeight * effectiveZoom),
  };
};

/**
 * Function to get the subtitle within the range.
 * @param {number} start
 * @param {number} end
 * @param {object} subtitleDatas
 * @returns Array of subtitles within the given range.
 */
export const getSubtitlesByDuration = (start, end, subtitleDatas) => {
  return Object.values(subtitleDatas)
    .filter(
      (subtitle) =>
        (subtitle.enterStart >= start && subtitle.exitEnd <= end) ||
        (subtitle.enterStart <= start && subtitle.exitEnd > start) ||
        (subtitle.enterStart < end && subtitle.enterStart > start)
    )
    .map(({ text }) => text);
};
